<template>
    <v-dialog :value="value" @input="$emit('input', $event)" max-width="700px" persistent scrollable>
        <v-card>
            <v-card-title class="headline primary white--text">
                <v-icon left color="white">mdi-plus</v-icon>
                Add Resource
                <v-spacer></v-spacer>
                <v-btn icon color="white" @click="closeDialog">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </v-card-title>

            <v-card-text class="pa-6">
                <v-form ref="uploadForm" v-model="formValid" lazy-validation>
                    <!-- Resource Type Mode Selection -->
                    <div class="mb-6">
                        <v-btn-toggle v-model="resourceMode" mandatory color="primary" class="mode-toggle mb-4">
                            <v-btn value="file" :class="{ 'mode-btn': true, 'active-mode': resourceMode === 'file' }">
                                <v-icon left>mdi-file-upload</v-icon>
                                File Upload
                            </v-btn>
                            <v-btn value="url" :class="{ 'mode-btn': true, 'active-mode': resourceMode === 'url' }">
                                <v-icon left>mdi-link</v-icon>
                                URL Resource
                            </v-btn>
                        </v-btn-toggle>
                    </div>

                    <!-- File Upload Section (File Mode) -->
                    <div v-if="resourceMode === 'file'" class="mb-6">
                        <v-file-input v-model="formData.file" :rules="fileRules" label="Select document to upload"
                            outlined show-size prepend-icon="" prepend-inner-icon="mdi-paperclip"
                            accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.zip,.png,.jpg,.jpeg"
                            :loading="uploading" @change="onFileChange" class="file-input">
                            <template v-slot:selection="{ text }">
                                <v-chip color="primary" outlined small close @click:close="formData.file = null">
                                    <v-icon left small>{{ getFileIcon(formData.file) }}</v-icon>
                                    {{ text }}
                                </v-chip>
                            </template>
                        </v-file-input>

                        <!-- File Upload Progress -->
                        <v-progress-linear v-if="uploadProgress > 0 && uploadProgress < 100" :value="uploadProgress"
                            color="primary" height="6" rounded class="mt-2"></v-progress-linear>
                    </div>

                    <!-- URL Input Section (URL Mode) -->
                    <div v-if="resourceMode === 'url'" class="mb-6">
                        <v-text-field 
                            v-model="formData.url" 
                            :rules="urlRules" 
                            label="Resource URL *" 
                            outlined 
                            dense 
                            prepend-inner-icon="mdi-link"
                            placeholder="https://docs.google.com/spreadsheets/d/..."
                            hint="Enter the URL for external resources like Google Sheets, documents, etc."
                            persistent-hint
                            :disabled="uploading">
                        </v-text-field>
                    </div>

                    <!-- Document Information -->
                    <v-row>
                        <v-col cols="12">
                            <v-text-field v-model="formData.title" :rules="titleRules" label="Document Title *" outlined
                                dense prepend-inner-icon="mdi-file-document-outline" maxlength="100" counter
                                :disabled="uploading"></v-text-field>
                        </v-col>

                        <v-col cols="12" md="6">
                            <v-select v-model="formData.category" :items="categoryOptions" :rules="categoryRules"
                                label="Category *" outlined dense prepend-inner-icon="mdi-tag"
                                :disabled="uploading"></v-select>
                        </v-col>

                        <v-col cols="12" md="6">
                            <v-select v-model="formData.company_id" :items="companyOptions" label="Company Access"
                                outlined dense prepend-inner-icon="mdi-office-building"
                                hint="Leave empty for shared access across all companies" persistent-hint clearable
                                :disabled="uploading"></v-select>
                        </v-col>

                        <v-col cols="12">
                            <v-textarea v-model="formData.description" label="Description" outlined rows="3"
                                prepend-inner-icon="mdi-text" maxlength="500" counter
                                hint="Brief description of the document content and purpose" persistent-hint
                                :disabled="uploading"></v-textarea>
                        </v-col>

                        <v-col cols="12">
                            <v-combobox v-model="formData.tags" :items="suggestedTags" label="Tags" outlined dense
                                multiple small-chips deletable-chips prepend-inner-icon="mdi-tag-multiple"
                                hint="Add tags to help categorize and search for this document" persistent-hint
                                :disabled="uploading">
                                <template v-slot:no-data>
                                    <v-list-item>
                                        <v-list-item-content>
                                            <v-list-item-title>
                                                Press <kbd>enter</kbd> to create a new tag
                                            </v-list-item-title>
                                        </v-list-item-content>
                                    </v-list-item>
                                </template>
                            </v-combobox>
                        </v-col>
                    </v-row>

                    <!-- File Information Display -->
                    <v-card v-if="formData.file" outlined class="mt-4 file-info-card">
                        <v-card-text class="pa-4">
                            <div class="d-flex align-center">
                                <v-icon :color="getFileTypeColor(getFileExtension(formData.file.name))" size="32"
                                    class="mr-3">
                                    {{ getFileIcon(formData.file) }}
                                </v-icon>
                                <div class="flex-grow-1">
                                    <h4 class="text-subtitle-1 font-weight-medium">{{ formData.file.name }}</h4>
                                    <div class="d-flex align-center text-caption grey--text">
                                        <span>{{ formatFileSize(formData.file.size) }}</span>
                                        <v-divider vertical class="mx-2"></v-divider>
                                        <span>{{ getFileExtension(formData.file.name).toUpperCase() }}</span>
                                        <v-divider vertical class="mx-2"></v-divider>
                                        <span>Last modified: {{ formatDate(formData.file.lastModified) }}</span>
                                    </div>
                                </div>
                                <v-chip :color="getFileSizeStatus(formData.file.size).color" small
                                    :text-color="getFileSizeStatus(formData.file.size).color === 'red' ? 'white' : 'black'">
                                    {{ getFileSizeStatus(formData.file.size).text }}
                                </v-chip>
                            </div>
                        </v-card-text>
                    </v-card>

                    <!-- Error Display -->
                    <v-alert v-if="errorMessage" type="error" dismissible @input="errorMessage = ''" class="mt-4">
                        {{ errorMessage }}
                    </v-alert>
                </v-form>
            </v-card-text>

            <v-card-actions class="pa-4">
                <v-spacer></v-spacer>
                <v-btn text @click="closeDialog" :disabled="uploading" class="mr-2">
                    Cancel
                </v-btn>
                <v-btn color="primary" :disabled="!formValid || uploading || (!formData.file && resourceMode === 'file') || (!formData.url && resourceMode === 'url')" :loading="uploading"
                    @click="uploadDocument">
                    <v-icon left>{{ resourceMode === 'file' ? 'mdi-cloud-upload' : 'mdi-plus' }}</v-icon>
                    {{ resourceMode === 'file' ? 'Upload Document' : 'Add Resource' }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
    name: 'UploadDocumentDialog',
    props: {
        value: {
            type: Boolean,
            default: false
        },
        presetCompanyId: {
            type: Number,
            default: null
        }
    },
    data() {
        return {
            formValid: false,
            uploading: false,
            uploadProgress: 0,
            errorMessage: '',
            resourceMode: 'file', // 'file' or 'url'
            formData: {
                file: null,
                url: '',
                title: '',
                description: '',
                category: '',
                company_id: null,
                tags: []
            },
            categoryOptions: [
                { text: 'Standard Operating Procedure', value: 'SOP' },
                { text: 'Template', value: 'Template' },
                { text: 'End of Day', value: 'EOD' },
                { text: 'Start of Day', value: 'SOD' },
                { text: 'Guide', value: 'Guide' },
                { text: 'Other', value: 'Other' }
            ],
            suggestedTags: [
                'important', 'urgent', 'training', 'policy', 'procedure', 'checklist',
                'template', 'guide', 'reference', 'daily', 'weekly', 'monthly',
                'operations', 'hr', 'finance', 'it', 'safety', 'compliance'
            ],
            // Validation rules
            fileRules: [
                v => this.resourceMode === 'url' || !!v || 'File is required',
                v => this.resourceMode === 'url' || !v || v.size <= 50000000 || 'File size must be less than 50MB',
                v => this.resourceMode === 'url' || !v || this.isValidFileType(v) || 'Invalid file type. Allowed: PDF, DOC, XLS, PPT, TXT, ZIP, Images'
            ],
            urlRules: [
                v => this.resourceMode === 'file' || !!v || 'URL is required',
                v => this.resourceMode === 'file' || !v || this.isValidUrl(v) || 'Please enter a valid URL (must start with http:// or https://)'
            ],
            titleRules: [
                v => !!v || 'Title is required',
                v => (v && v.length >= 3) || 'Title must be at least 3 characters',
                v => (v && v.length <= 100) || 'Title must be less than 100 characters'
            ],
            categoryRules: [
                v => !!v || 'Category is required'
            ]
        };
    },
    computed: {
        ...mapGetters('companies', [
            'get_companies'
        ]),
        companyOptions() {
            const companies = this.get_companies || [];
            return companies.map(company => ({
                text: company.name,
                value: company.id
            }));
        }
    },
    watch: {
        value(newVal) {
            if (newVal) {
                this.resetForm();
                // Set preset company if provided
                if (this.presetCompanyId) {
                    this.formData.company_id = this.presetCompanyId;
                }
                // Fetch companies when dialog opens if not already loaded
                if (!this.get_companies || this.get_companies.length === 0) {
                    this.fetchCompanies();
                }
            }
        }
    },
    methods: {
        async fetchCompanies() {
            try {
                const response = await this.$axios.get('api/companies/get_companies');
                if (response.data.response) {
                    this.$store.dispatch("companies/set_companies", response.data.data);
                }
            } catch (error) {
                console.error('Error fetching companies:', error);
            }
        },

        async uploadDocument() {
            if (!this.$refs.uploadForm.validate()) {
                return;
            }

            // Prepare form data for upload based on mode
            const uploadData = {
                name: this.formData.title,
                description: this.formData.description,
                category: this.formData.category,
                company_id: this.formData.company_id,
                tags: this.formData.tags,
                type: this.resourceMode, // 'file' or 'url'
                created_by: 'Current User', // This should come from auth state
            };

            if (this.resourceMode === 'file') {
                // File-based resource
                uploadData.file = this.formData.file;
                uploadData.file_type = this.getFileExtension(this.formData.file.name);
                uploadData.size = this.formatFileSize(this.formData.file.size);
            } else {
                // URL-based resource
                uploadData.url = this.formData.url;
                uploadData.file_type = this.getUrlFileType(this.formData.url);
                uploadData.size = null; // No size for URL resources
            }

            // Emit upload data to parent component
            // Parent will handle the actual upload, progress, success/error states, and dialog closing
            this.$emit('upload-document', uploadData);
        },

        onFileChange(file) {
            if (file && !this.formData.title) {
                // Auto-populate title from filename
                const nameWithoutExt = file.name.replace(/\.[^/.]+$/, '');
                this.formData.title = nameWithoutExt.replace(/[_-]/g, ' ');
            }
        },

        isValidUrl(url) {
            try {
                new URL(url);
                return url.startsWith('http://') || url.startsWith('https://');
            } catch {
                return false;
            }
        },

        getUrlFileType(url) {
            // Extract file type from URL or determine based on common patterns
            if (url.includes('docs.google.com/spreadsheets')) return 'google-sheets';
            if (url.includes('docs.google.com/document')) return 'google-docs';
            if (url.includes('docs.google.com/presentation')) return 'google-slides';
            if (url.includes('drive.google.com')) return 'google-drive';
            if (url.includes('sharepoint.com') || url.includes('office.com')) return 'office-365';
            
            // Try to extract file extension from URL
            const urlPath = new URL(url).pathname;
            const extension = urlPath.split('.').pop().toLowerCase();
            const validExtensions = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'];
            
            return validExtensions.includes(extension) ? extension : 'url';
        },

        isValidFileType(file) {
            const allowedTypes = [
                'application/pdf',
                'application/msword',
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                'application/vnd.ms-excel',
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                'application/vnd.ms-powerpoint',
                'application/vnd.openxmlformats-officedocument.presentationml.presentation',
                'text/plain',
                'application/zip',
                'image/png',
                'image/jpeg',
                'image/jpg'
            ];

            const allowedExtensions = [
                '.pdf', '.doc', '.docx', '.xls', '.xlsx',
                '.ppt', '.pptx', '.txt', '.zip', '.png', '.jpg', '.jpeg'
            ];

            return allowedTypes.includes(file.type) ||
                allowedExtensions.some(ext => file.name.toLowerCase().endsWith(ext));
        },

        getFileIcon(file) {
            if (!file) return 'mdi-file-document-outline';

            const ext = this.getFileExtension(file.name);
            const iconMap = {
                pdf: 'mdi-file-pdf-box',
                doc: 'mdi-file-word-box',
                docx: 'mdi-file-word-box',
                xls: 'mdi-file-excel-box',
                xlsx: 'mdi-file-excel-box',
                ppt: 'mdi-file-powerpoint-box',
                pptx: 'mdi-file-powerpoint-box',
                txt: 'mdi-file-document-outline',
                zip: 'mdi-folder-zip',
                png: 'mdi-file-image-box',
                jpg: 'mdi-file-image-box',
                jpeg: 'mdi-file-image-box',
                // URL-based icons
                'google-sheets': 'mdi-google-spreadsheet',
                'google-docs': 'mdi-file-document',
                'google-slides': 'mdi-google-slides',
                'google-drive': 'mdi-google-drive',
                'office-365': 'mdi-microsoft-office',
                'url': 'mdi-link'
            };
            return iconMap[ext] || 'mdi-file-document-outline';
        },

        getFileTypeColor(fileType) {
            const colorMap = {
                pdf: 'red',
                doc: 'blue',
                docx: 'blue',
                xls: 'green',
                xlsx: 'green',
                ppt: 'orange',
                pptx: 'orange',
                txt: 'grey',
                zip: 'purple',
                png: 'pink',
                jpg: 'pink',
                jpeg: 'pink'
            };
            return colorMap[fileType] || 'grey';
        },

        getFileExtension(filename) {
            return filename.slice((filename.lastIndexOf('.') - 1 >>> 0) + 2).toLowerCase();
        },

        formatFileSize(bytes) {
            if (bytes === 0) return '0 Bytes';
            const k = 1024;
            const sizes = ['Bytes', 'KB', 'MB', 'GB'];
            const i = Math.floor(Math.log(bytes) / Math.log(k));
            return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
        },

        formatDate(timestamp) {
            return new Date(timestamp).toLocaleDateString();
        },

        getFileSizeStatus(size) {
            const mb = size / (1024 * 1024);
            if (mb > 50) return { color: 'red', text: 'Too Large' };
            if (mb > 25) return { color: 'orange', text: 'Large' };
            if (mb > 10) return { color: 'yellow', text: 'Medium' };
            return { color: 'green', text: 'Good' };
        },

        resetForm() {
            this.formData = {
                file: null,
                url: '',
                title: '',
                description: '',
                category: '',
                company_id: null,
                tags: []
            };
            this.resourceMode = 'file'; // Reset to default mode
            this.errorMessage = '';
            this.uploadProgress = 0;
            this.uploading = false;
            if (this.$refs.uploadForm) {
                this.$refs.uploadForm.resetValidation();
            }
        },

        closeDialog() {
            if (!this.uploading) {
                this.$emit('input', false);
                this.resetForm();
            }
        }
    }
};
</script>

<style scoped>
.file-input>>>.v-file-input__text {
    color: rgba(0, 0, 0, 0.87);
}

.file-info-card {
    background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
    border: 1px dashed #1976d2;
}

.file-info-card:hover {
    box-shadow: 0 4px 12px rgba(25, 118, 210, 0.15);
}

/* Mode toggle styling */
.mode-toggle {
    width: 100%;
    background-color: #f5f5f5;
    border-radius: 8px;
    padding: 4px;
}

.mode-btn {
    flex: 1;
    text-transform: none;
    font-weight: 500;
    border-radius: 6px !important;
    margin: 0 2px;
}

.mode-btn.active-mode {
    background-color: #1976d2 !important;
    color: white !important;
    box-shadow: 0 2px 4px rgba(25, 118, 210, 0.3);
}

/* Upload progress styling */
.v-progress-linear {
    border-radius: 4px;
}

/* Form spacing */
.v-text-field,
.v-select,
.v-textarea,
.v-file-input,
.v-combobox {
    margin-bottom: 8px;
}

/* Dialog styling */
.v-dialog>.v-card>.v-card__title {
    border-radius: 4px 4px 0 0;
}

/* File chip styling */
.v-chip {
    max-width: 200px;
}

/* Mobile responsiveness */
@media (max-width: 600px) {
    .v-dialog {
        margin: 16px;
    }

    .file-info-card .text-caption {
        font-size: 10px;
    }
}
</style>